// PipelineInferno - AWS DevOps
// Histoire : Le pipeline CI/CD explose en production. 500 erreurs, le SRE est en alerte.

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface PipelineInfernoScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  coverImage: ReturnType<typeof require>;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const pipelineInfernoCharacters: Character[] = [
  { id: 'viktor', name: 'Viktor', role: 'DevOps Manager',  color: '#1E3A5F', avatar: 'viktor' },
  { id: 'liwei',  name: 'Liwei',  role: 'SRE Lead',        color: '#065F46', avatar: 'liwei'  },
  { id: 'priya',  name: 'Priya',  role: 'Senior Developer', color: '#7C2D12', avatar: 'priya'  },
  { id: 'katya',  name: 'Katya',  role: 'QA Lead',          color: '#4338CA', avatar: 'katya'  },
];

const scene1: SequenceItem = {
  type: 'story',
  id: 'pi-scene-1',
  title: "Scene 1 : Vendredi 18h — tout tombe",
  duration: 4,
  content: {
    narrativeText:
      "Vendredi 18h02. Le deploiement est parti automatiquement.\n\n" +
      "**Liwei :** \"Viktor ! Error rate sur prod : 12% et en hausse. 500 erreurs en cascade depuis 3 minutes.\"\n\n" +
      "**Viktor :** \"Le deploiement de 18h00 ?\"\n\n" +
      "**Priya :** \"C'etait le mien. La feature de paiement multi-devise. Les tests passaient tous en staging.\"\n\n" +
      "**Liwei :** \"Staging n'a pas de vraies donnees de prod. La base prod a des encodages UTF-8 exotiques que staging n'a pas.\"\n\n" +
      "**Viktor :** \"Option 1 : rollback immediat. Option 2 : hotfix en urgence. On a 5 minutes avant que le monitoring alerte les clients.\"\n\n" +
      "**Priya :** \"Hotfix. Je connais exactement le bug. 15 minutes.\"\n\n" +
      "**Liwei :** \"Rollback. MTTR moyen pour un hotfix non teste est de 47 minutes. On ne peut pas se permettre.\"\n\n" +
      "**Viktor :** \"Liwei a raison. Rollback maintenant. Priya, hotfix avec tests cette fois.\"\n\n" +
      "**Katya :** \"Et la prochaine fois on deploie pas le vendredi soir. C'est une regle DevOps basique.\"\n\n" +
      "**Viktor :** \"Rollback en cours. Et apres : on implémente le blue-green deployment pour eviter ca.\"",
    characters: ['viktor', 'liwei', 'priya', 'katya'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'pi-card-1',
  title: 'Concept : Blue-Green vs Canary Deployment',
  duration: 5,
  content: {
    title: 'Strategies de deploiement : Blue-Green et Canary',
    body:
      "## Strategies de deploiement\n\n" +
      "### Blue-Green Deployment\n" +
      "Deux environnements identiques : Blue (prod actuelle) et Green (nouvelle version).\n\n" +
      "```\n[Load Balancer]\n     |\n   / | \\\n  Blue Green  (Green recoit 0% du trafic)\n     |\n  Switch instantane\n     |\n  Green (100%) — Blue devient le backup\n```\n\n" +
      "**Avantages** : Rollback instantane (re-switch), zero downtime\n" +
      "**Inconvenients** : Double cout infrastructure, problemes de migration de DB\n\n" +
      "### Canary Deployment\n" +
      "Deploiement progressif sur un sous-ensemble d'utilisateurs.\n\n" +
      "```\nV1 (95% trafic) + V2 (5% trafic)\n→ V1 (80%) + V2 (20%)\n→ V1 (0%) + V2 (100%)\n```\n\n" +
      "**Avantages** : Risque limite, validation en conditions reelles, feedback progressif\n" +
      "**Inconvenients** : Complexite du routing, besoin de feature flags\n\n" +
      "### Comparaison\n" +
      "| Critere | Blue-Green | Canary |\n" +
      "|---|---|---|\n" +
      "| Rollback | Instantane | Progressif |\n" +
      "| Risque | Faible | Tres faible |\n" +
      "| Cout infra | Double | +20% |\n" +
      "| Complexite | Moyenne | Elevee |\n" +
      "| Adapte si | Deploys critiques | Features incrementales |\n\n" +
      "### AWS : CodeDeploy\n" +
      "Supporte nativement Blue-Green et Canary pour EC2, ECS et Lambda.",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'pi-chat-1',
  title: 'Discussion DevOps : Rollback vs Hotfix',
  duration: 6,
  content: {
    tutorCharacterId: 'viktor',
    messages: [
      {
        id: 'pi-chat-1-q1',
        sender: 'character',
        characterId: 'viktor',
        text: "Liwei prefere le rollback, Priya prefere le hotfix. Quel critere devrait guider cette decision ?",
        type: 'single-choice',
        options: [
          "La preference du developpeur qui a casse le build",
          "Le MTTR compare (Mean Time To Recover) de chaque option",
          "La solution qui preserve le plus de features",
          "L'opinion du manager",
        ],
        correctAnswer: "Le MTTR compare (Mean Time To Recover) de chaque option",
        explanation: "En incident de prod, la metrique cle est le MTTR. Un rollback = temps connu (2-5 min). Un hotfix = temps incertain + risque d'introduire un nouveau bug non teste. La regle d'or : 'Rollback first, fix later.'",
      },
      {
        id: 'pi-chat-1-q2',
        sender: 'character',
        characterId: 'viktor',
        text: "Quels sont les avantages du Blue-Green deployment par rapport au deploiement classique ?",
        type: 'multiple-choice',
        options: [
          "Rollback instantane sans downtime",
          "Validation complete en production avant le switch",
          "Reduction des couts infrastructure de 50%",
          "Zero downtime lors du deploiement",
        ],
        correctAnswer: [
          "Rollback instantane sans downtime",
          "Validation complete en production avant le switch",
          "Zero downtime lors du deploiement",
        ],
        explanation: "Blue-Green permet de valider la nouvelle version avec du trafic reel avant le switch complet, et de rollback instantanement en cas de probleme. L'infrastructure double le cout (pas de reduction).",
      },
      {
        id: 'pi-chat-1-q3',
        sender: 'character',
        characterId: 'viktor',
        text: "Quel service AWS permet de configurer un deploiement Canary sur une fonction Lambda ?",
        type: 'single-choice',
        options: [
          "AWS Elastic Beanstalk",
          "AWS CodeDeploy avec Lambda deployment group",
          "AWS CloudFormation avec canary option",
          "Amazon ECS avec rolling update",
        ],
        correctAnswer: "AWS CodeDeploy avec Lambda deployment group",
        explanation: "AWS CodeDeploy supporte les strategies Canary et Linear pour Lambda. Exemple : LambdaCanary10Percent5Minutes — 10% du trafic sur la nouvelle version pendant 5 minutes avant le shift complet.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'pi-task-1',
  title: 'Configurer un pipeline CI/CD avec rollback automatique',
  duration: 35,
  content: {
    platform: 'AWS Console — CodePipeline + CodeDeploy',
    objective: "Configurer un pipeline CI/CD avec deploiement Blue-Green et rollback automatique",
    azurePortalPath: 'console.aws.amazon.com > CodePipeline > Create pipeline',
    steps: [
      {
        stepNumber: 1,
        action: "Creer le pipeline CodePipeline",
        detail: "CodePipeline > Create pipeline. Name = 'payment-service-pipeline'. Source : GitHub (main branch). Build : CodeBuild. Deploy : CodeDeploy.",
      },
      {
        stepNumber: 2,
        action: "Configurer CodeDeploy pour Blue-Green",
        detail: "CodeDeploy > Application > Create deployment group. Deployment type = 'Blue/green'. Traffic routing : ELB-based. Terminate original instances : After 15 minutes.",
      },
      {
        stepNumber: 3,
        action: "Configurer les alarmes CloudWatch",
        detail: "CloudWatch > Alarms > Create: 'prod-error-rate-high' (metric: 5xx errors > 5% sur 5 minutes). Associer a CodeDeploy pour rollback automatique.",
      },
      {
        stepNumber: 4,
        action: "Definir le rollback automatique",
        detail: "CodeDeploy deployment group > Rollback configuration. Enable rollback: ON. Roll back when deployment fails: checked. Roll back when monitored alarms are activated: 'prod-error-rate-high'.",
      },
      {
        stepNumber: 5,
        action: "Tester le pipeline",
        detail: "Pousser un commit de test. Verifier le deploiement Blue-Green. Introduire volontairement une erreur — verifier que le rollback se declenche automatiquement.",
      },
    ],
    validation: "Pipeline active, Blue-Green configure, rollback automatique declenche en < 5 min sur alarm.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'pi-quiz-1',
  title: 'Quiz : DevOps et Strategies de deploiement',
  duration: 5,
  content: {
    questions: [
      {
        id: 'pi-q1-1',
        question: "Qu'est-ce que le MTTR (Mean Time To Recovery) mesure ?",
        type: 'single-choice',
        options: [
          "Le temps moyen entre deux incidents",
          "Le temps moyen pour restaurer un service apres une panne",
          "Le taux de success des deployments",
          "La frequence des releases",
        ],
        correctAnswer: "Le temps moyen pour restaurer un service apres une panne",
        explanation: "MTTR = temps moyen pour detecter, diagnostiquer et corriger un incident. Objectif DevOps : MTTR < 1h. Se distingue du MTBF (Mean Time Between Failures) qui mesure la fiabilite.",
      },
      {
        id: 'pi-q1-2',
        question: "Pourquoi ne devrait-on generalement pas deployer le vendredi soir ?",
        type: 'single-choice',
        options: [
          "Les serveurs sont moins performants le vendredi",
          "Les equipes sont moins disponibles pour repondre a un incident en dehors des heures ouvrables",
          "Le trafic est plus eleve le vendredi",
          "Les deploiements du vendredi ont plus de bugs",
        ],
        correctAnswer: "Les equipes sont moins disponibles pour repondre a un incident en dehors des heures ouvrables",
        explanation: "Si un deploiement du vendredi soir genere un incident, l'equipe est indisponible pour le week-end. Le MTTR explose. La regle DevOps : deployer quand l'equipe complete est disponible pour reagir.",
      },
      {
        id: 'pi-q1-3',
        question: "Dans un deploiement Canary, comment decider d'augmenter progressivement le trafic vers la nouvelle version ?",
        type: 'single-choice',
        options: [
          "Augmentation lineaire automatique avec un timer fixe",
          "Basee sur des metriques (error rate, latence, satisfaction) sur le trafic Canary",
          "Decision manuelle uniquement du lead devops",
          "Augmentation apres chaque commit valide",
        ],
        correctAnswer: "Basee sur des metriques (error rate, latence, satisfaction) sur le trafic Canary",
        explanation: "Le Canary doit etre pilote par des metriques. Si la nouvelle version maintient error rate < 1% et latence P99 < 200ms sur 5% du trafic, on augmente. Si les metriques se degradent, rollback automatique.",
      },
    ],
  } as QuizContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: "L'Incendie",
  subtitle: 'AWS DevOps — CI/CD, rollback et strategies de deploiement',
  learningObjectives: [
    "Comprendre Blue-Green et Canary deployment",
    "Configurer AWS CodeDeploy avec rollback automatique",
    "Appliquer la strategie MTTR pour les decisions d'incident",
    "Creer des alarmes CloudWatch pour le monitoring prod",
    "Implementer un pipeline CodePipeline complet",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1],
};

export const pipelineInfernoScenario: PipelineInfernoScenario = {
  id: 'pipeline-inferno',
  title: 'Pipeline Inferno',
  subtitle: "Le deploiement du vendredi soir qui part en flammes",
  certification: 'AWS DevOps',
  coverImage: require('../../../assets/images/characters/nina_authority.png'),
  characters: pipelineInfernoCharacters,
  chapters: [chapter1],
};

export default pipelineInfernoScenario;
